// Açıklamalar ⓘ düğmesinin arkasında (7.14 · D-654).
//
// Batuhan: "o bilgiyi gören bir kişi artık ihtiyaç duymayabilir ve o yüzden hep
// açık olmamalıdır. Uygulamanın tamamında bu politika güdülmelidir."
//
// KURAL: bir ayarın ya da seçeneğin ne işe yaradığını anlatan metin her zaman
// görünmez. Etiketle AYNI SATIRDA küçük bir ⓘ durur; dokununca açıklama
// satırın altında açılır, yine dokununca kapanır. Ekran her açıldığında kapalı
// başlar: açıklamayı bir kez okuyan onu bir daha görmek zorunda kalmaz.
//
// GÖRÜNÜR KALANLAR açıklama değildir: fiyat, sınır, bakiye, durum, hata ve
// bir işlemin anında bilinmesi gereken uyarı. Onlar ⓘ arkasına girmez.
//
// Açıklama metinlerinin anahtarı `Info` ile biter ve yalnız buradan çizilir;
// `__tests__/infoHint.test.tsx` bunu bütün kaynakta denetliyor.
import React, { useCallback, useState } from 'react';
import { StyleSheet, Text, TouchableOpacity, View } from './rn';
import type { StyleProp, TextStyle, ViewStyle } from 'react-native';
import Ionicons from '@expo/vector-icons/Ionicons';
import { fontSize, spacing } from '../theme';
import { useTheme } from '../theme/ThemeContext';
import { useTranslation } from '../i18n/I18nContext';

/** Tek bir açıklamanın açık/kapalı durumu. */
export function useInfo() {
  const [open, setOpen] = useState(false);
  const toggle = useCallback(() => setOpen((value) => !value), []);
  return { open, toggle };
}

/** Bir listedeki açıklamalar: her satırın kendi anahtarı. */
export function useInfoSet() {
  const [openKeys, setOpenKeys] = useState<ReadonlySet<string>>(new Set());
  const isOpen = useCallback((key: string) => openKeys.has(key), [openKeys]);
  const toggle = useCallback((key: string) => {
    setOpenKeys((current) => {
      const next = new Set(current);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }, []);
  return { isOpen, toggle };
}

/**
 * Etiketin hemen yanına konan ⓘ. Görünen simge küçük, dokunma alanı 44 pt:
 * satırı kalabalıklaştırmadan parmakla rahat bulunur.
 */
export function InfoButton({
  open,
  onPress,
  subject,
  testID,
}: {
  open: boolean;
  onPress: () => void;
  /** Ekran okuyucu için: neyin açıklaması. */
  subject: string;
  testID?: string;
}) {
  const colors = useTheme();
  const { t } = useTranslation();
  return (
    <TouchableOpacity
      testID={testID}
      onPress={onPress}
      accessibilityRole="button"
      accessibilityLabel={t(open ? 'common.infoHide' : 'common.infoShow', { subject })}
      accessibilityState={{ expanded: open }}
      hitSlop={{ top: 13, bottom: 13, left: 10, right: 13 }}
      style={styles.button}>
      <Ionicons
        name={open ? 'information-circle' : 'information-circle-outline'}
        size={18}
        color={open ? colors.black : colors.gray600}
      />
    </TouchableOpacity>
  );
}

/** Açıklamanın kendisi; kapalıyken hiç çizilmez. */
export function InfoText({
  open,
  text,
  style,
  testID,
}: {
  open: boolean;
  text: string;
  style?: StyleProp<TextStyle>;
  testID?: string;
}) {
  const colors = useTheme();
  if (!open) return null;
  return (
    <Text testID={testID} style={[styles.text, { color: colors.gray600 }, style]}>
      {text}
    </Text>
  );
}

/**
 * En sık durum: tek başına duran bir etiket (seçenek grubunun başlığı, bir
 * alanın adı). Etiket + ⓘ aynı satırda, açıklama altında.
 */
export function LabelWithInfo({
  label,
  info,
  labelStyle,
  style,
  testID,
}: {
  label: string;
  info: string;
  labelStyle?: StyleProp<TextStyle>;
  style?: StyleProp<ViewStyle>;
  testID?: string;
}) {
  const { open, toggle } = useInfo();
  return (
    <View style={style}>
      <View style={styles.labelRow}>
        <Text style={[labelStyle, styles.labelText]}>{label}</Text>
        <InfoButton open={open} onPress={toggle} subject={label} testID={testID ? `${testID}-info` : undefined} />
      </View>
      <InfoText open={open} text={info} testID={testID ? `${testID}-info-text` : undefined} />
    </View>
  );
}

const styles = StyleSheet.create({
  button: { marginLeft: 6, justifyContent: 'center', alignItems: 'center' },
  text: { fontSize: fontSize.sm, lineHeight: 19, marginTop: 2, marginBottom: spacing.xs },
  labelRow: { flexDirection: 'row', alignItems: 'center' },
  labelText: { flexShrink: 1 },
});
